import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Modal,
  Button,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useBondLadderStore } from "../../Store/bondLadderStore";
import BondLadderTab from "../OrderEntry/BondLadderTab";
import { useAuth } from "../../Context/AuthProvider";
import { updateBondLadder } from "../../http/api"; // Assuming this API exists
import { headerStyle, modalStyle } from "../OrderEntry/styles";
import { filterObjectByBondType } from "../../Utils/bondTypeFieldFilter";
import {
  toBackendFilterKeys,
  withUiFilterKeyAliases,
} from "../../Utils/filterKeyAliases";
import { tokens } from "../../Common/designTokens";
const EditBondLadder = ({ open, onClose, ladderData, onSubmit }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const { userId } = useAuth();
  const [
    hasCharacteristicValidationErrors,
    setHasCharacteristicValidationErrors,
  ] = useState(false); // Add this state

  const {
    bondLadderData,
    setBondLadderData,
    resetBondLadderData,
    setChecked,
    checked: storeChecked,
    fieldErrors,
    setFieldErrors,
    clearFieldErrors,
    validationErrors: storeValidationErrors,
    clearValidationErrors,
  } = useBondLadderStore();

  // Prefill data when modal opens
  useEffect(() => {
    if (open && ladderData) {
      // Reset first to clear any previous state
      console.log("ladder data ", ladderData);
      resetBondLadderData();

      // Parse characteristics if it exists
      let parsedCharacteristics = {};
      if (ladderData.characteristics) {
        try {
          if (typeof ladderData.characteristics === "string") {
            parsedCharacteristics = JSON.parse(ladderData.characteristics);
          } else if (typeof ladderData.characteristics === "object") {
            parsedCharacteristics = ladderData.characteristics;
          }
        } catch (error) {
          console.error("Error parsing characteristics:", error);
          parsedCharacteristics = {};
        }
      }
      // The form below is keyed by the UI spelling (couponMin, ...) while the stored JSON
      // carries the backend spelling (minCoupon, ...); accept either
      parsedCharacteristics = withUiFilterKeyAliases(parsedCharacteristics);

      // Use parsedCharacteristics from the data if available, otherwise fallback to direct properties
      const characteristics =
        ladderData.parsedCharacteristics || parsedCharacteristics;
      const parseDurationField = (durationField) => {
        let comparison = "";
        let value = "";

        if (durationField.startsWith(">=")) {
          comparison = "greaterThanOrEqual";
          value = durationField.replace(">=", "").trim();
        } else if (durationField.startsWith("<=")) {
          comparison = "lessThanOrEqual";
          value = durationField.replace("<=", "").trim();
        } else {
          value = durationField;
        }

        return { comparison, value };
      };
      // Handle duration field specially to extract operator and value
      let durationValue = "";
      let durationComparison = "";
      const durationField =
        characteristics.duration || ladderData.duration || "";

      if (durationField) {
        const parsedDuration = parseDurationField(durationField);
        durationValue = parsedDuration.value;
        durationComparison = parsedDuration.comparison;
      }

      // Map the incoming ladder data to the store format
      const mappedData = {
        side: ladderData.side || "BUY",
        account: ladderData.account || "",
        bondType: ladderData.type || ladderData.bondType || "Corporates",
        comments: ladderData.comments || "",
        nameOfTheLadder: ladderData.nameOfTheLadder || "",
        numberOfRungs: ladderData.numberOfRungs || "",
        qty: ladderData.qty || "",
        amountPerRung: ladderData.amountPerRung || "",
        totalMaturity: ladderData.totalMaturity || "",
        duration: durationField, // Keep the original format for the duration field
        durationComparison: durationComparison, // Add the extracted comparison
        couponMin: characteristics.couponMin || ladderData.couponMin || "",
        couponMax: characteristics.couponMax || ladderData.couponMax || "",
        maturityMin:
          characteristics.maturityMin || ladderData.maturityMin || "",
        maturityMax:
          characteristics.maturityMax || ladderData.maturityMax || "",
        minimumRating:
          characteristics.minimumRating || ladderData.minimumRating || "",
        zeroCoupon: characteristics.zeroCoupon || ladderData.zeroCoupon || "",
        callable: characteristics.callable || ladderData.callable || "",
        state: characteristics.state || ladderData.state || "",
        underlyingRating:
          characteristics.underlyingRating || ladderData.underlyingRating || "",
        insured: characteristics.insured || ladderData.insured || "",
        taxable: characteristics.taxable || ladderData.taxable || "",
        paymentFrequency:
          characteristics.paymentFrequency || ladderData.paymentFrequency || "",
        incomeSource:
          characteristics.incomeSource || ladderData.incomeSource || "",
        altMinTax: characteristics.altMinTax || ladderData.altMinTax || "",
        securityCharge: (() => {
          const sc = characteristics.securityCharge || ladderData.securityCharge;
          if (!sc) return ["ALL"];
          if (Array.isArray(sc)) return sc.length > 0 ? sc : ["ALL"];
          return [sc];
        })(),
        sector: characteristics.sector || ladderData.sector || "",
        nextCallDate:
          characteristics.nextCallDate || ladderData.nextCallDate || "",
        priceMin: characteristics.priceMin || ladderData.priceMin || "",
        priceMax: characteristics.priceMax || ladderData.priceMax || "",
        yieldMin: characteristics.yieldMin || ladderData.yieldMin || "",
        yieldMax: characteristics.yieldMax || ladderData.yieldMax || "",
        // pm: ladderData.pm || "",
        // pmId: ladderData.pmId || "",
        userId: userId || null,
        userName: ladderData.userName || null,
        bondLadderId: ladderData.bondLadderId || null,
        issuer: characteristics.issuer || ladderData.issuer || "",
        minimumRatingMoody:
          characteristics.minimumRatingMoody ||
          ladderData.minimumRatingMoody ||
          "",
      };

      setBondLadderData(mappedData);

      // Set checkbox states based on populated values
      setTimeout(() => {
        const checkboxesToSet = {
          bondLadder: {},
        };

        // Check which fields have values and set corresponding checkboxes
        if (mappedData.couponMin || mappedData.couponMax) {
          checkboxesToSet.bondLadder.couponRange = true;
        }
        if (mappedData.maturityMin || mappedData.maturityMax) {
          checkboxesToSet.bondLadder.maturityRanges = true;
        }
        if (mappedData.priceMin || mappedData.priceMax) {
          checkboxesToSet.bondLadder.price = true;
        }
        if (mappedData.yieldMin || mappedData.yieldMax) {
          checkboxesToSet.bondLadder.yield = true;
        }
        if (mappedData.minimumRating) {
          checkboxesToSet.bondLadder.minimumRating = true;
        }
        if (mappedData.minimumRatingMoody) {
          checkboxesToSet.bondLadder.minimumRatingMoody = true;
        }
        if (mappedData.zeroCoupon) {
          checkboxesToSet.bondLadder.zeroCoupon = true;
        }
        if (mappedData.callable) {
          checkboxesToSet.bondLadder.callable = true;
        }
        if (mappedData.state) {
          checkboxesToSet.bondLadder.state = true;
        }
        if (mappedData.underlyingRating) {
          checkboxesToSet.bondLadder.underlyingRating = true;
        }
        if (mappedData.insured) {
          checkboxesToSet.bondLadder.insured = true;
        }
        if (mappedData.taxable) {
          checkboxesToSet.bondLadder.taxable = true;
        }
        if (mappedData.paymentFrequency) {
          checkboxesToSet.bondLadder.paymentFrequency = true;
        }
        if (mappedData.incomeSource) {
          checkboxesToSet.bondLadder.incomeSource = true;
        }
        if (mappedData.altMinTax) {
          checkboxesToSet.bondLadder.altMinTax = true;
        }
        if (!!(characteristics.securityCharge || ladderData.securityCharge)) {
          checkboxesToSet.bondLadder.securityCharge = true;
        }
        if (mappedData.sector) {
          checkboxesToSet.bondLadder.sector = true;
        }
        if (mappedData.nextCallDate) {
          checkboxesToSet.bondLadder.nextCallDate = true;
        }
        if (mappedData.issuer) {
          checkboxesToSet.bondLadder.issuer = true;
        }
        if (mappedData.duration) {
          checkboxesToSet.bondLadder.duration = true;
        }

        // Set all checkboxes
        Object.entries(checkboxesToSet.bondLadder).forEach(
          ([field, isChecked]) => {
            setChecked("bondLadder", field, isChecked);
          }
        );
      }, 100);
    }
  }, [open, ladderData, resetBondLadderData, setBondLadderData, setChecked]);

  // Clear validation errors when modal opens
  useEffect(() => {
    if (open) {
      clearValidationErrors();
      setHasCharacteristicValidationErrors(false);
    }
  }, [open, clearValidationErrors]);

  // Monitor store validation errors
  useEffect(() => {
    const hasErrors = Object.values(storeValidationErrors).some(Boolean);
    setHasCharacteristicValidationErrors(hasErrors);
  }, [storeValidationErrors]);

  const validateForm = () => {
    const errors = {};

    if (!bondLadderData.nameOfTheLadder?.trim()) {
      errors.nameOfTheLadder = true;
    }
    if (!bondLadderData.numberOfRungs || bondLadderData.numberOfRungs <= 0) {
      errors.numberOfRungs = true;
    }
    if (!bondLadderData.amountPerRung || bondLadderData.amountPerRung <= 0) {
      errors.amountPerRung = true;
    }
    if (!bondLadderData.totalMaturity || bondLadderData.totalMaturity <= 0) {
      errors.totalMaturity = true;
    }
    if (!bondLadderData.account?.trim()) {
      errors.account = true;
    }

    setValidationErrors(errors);
    setFieldErrors(errors);

    // Check both basic validation and characteristic validation errors
    const hasBasicErrors = Object.keys(errors).length > 0;
    const hasCharacteristicErrors = hasCharacteristicValidationErrors;
    const hasWindowErrors =
      typeof window !== "undefined" && window.bondValidationErrors
        ? Object.values(window.bondValidationErrors).some(Boolean)
        : false;

    return !hasBasicErrors && !hasCharacteristicErrors && !hasWindowErrors;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      if (hasCharacteristicValidationErrors) {
        setError(
          "Please fix the validation errors in the characteristics fields before submitting."
        );
      } else {
        setError("Please fill in all required fields.");
      }
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Extract characteristics fields from bondLadderData
      const characteristicsFields = {
        couponMin: bondLadderData.couponMin,
        couponMax: bondLadderData.couponMax,
        maturityMin: bondLadderData.maturityMin,
        maturityMax: bondLadderData.maturityMax,
        minimumRating: bondLadderData.minimumRating,
        zeroCoupon: bondLadderData.zeroCoupon,
        callable: bondLadderData.callable,
        state: bondLadderData.state,
        underlyingRating: bondLadderData.underlyingRating,
        insured: bondLadderData.insured,
        taxable: bondLadderData.taxable,
        paymentFrequency: bondLadderData.paymentFrequency,
        incomeSource: bondLadderData.incomeSource,
        altMinTax: bondLadderData.altMinTax,
        securityCharge: (() => {
          const sc = bondLadderData.securityCharge;
          if (!sc || (Array.isArray(sc) && sc.length === 0)) return '';
          if (Array.isArray(sc)) return sc.includes("ALL") ? "ALL" : sc;
          return sc;
        })(),
        sector: bondLadderData.sector,
        nextCallDate: bondLadderData.nextCallDate,
        priceMin: bondLadderData.priceMin,
        priceMax: bondLadderData.priceMax,
        yieldMin: bondLadderData.yieldMin,
        yieldMax: bondLadderData.yieldMax,
        issuer: bondLadderData.issuer,
        minimumRatingMoody: bondLadderData.minimumRatingMoody,
        duration: bondLadderData.duration,
      };

      // Enforce checkbox gating — only include fields the user checked
      const checkedFields = (storeChecked && storeChecked['bondLadder']) || {};
      const getCheckboxKey = (f) => {
        if (f === 'couponMin' || f === 'couponMax') return 'couponRange';
        if (f === 'maturityMin' || f === 'maturityMax') return 'maturityRanges';
        if (f === 'priceMin' || f === 'priceMax') return 'price';
        if (f === 'yieldMin' || f === 'yieldMax') return 'yield';
        return f;
      };
      Object.keys(characteristicsFields).forEach((f) => {
        if (!checkedFields[getCheckboxKey(f)]) delete characteristicsFields[f];
      });

      // NEW: remove fields not supported for selected bond type
      const filteredByType = filterObjectByBondType(bondLadderData.bondType, characteristicsFields);

      // Filter out empty/null/undefined values
      const filteredCharacteristics = Object.entries(filteredByType)
        .filter(
          ([, value]) =>
            value !== "" && value !== null && value !== undefined
        )
        .reduce((obj, [key, value]) => {
          obj[key] = value;
          return obj;
        }, {});

      // Prepare data for API call
      const updateData = {
        side: bondLadderData.side || "BUY",
        account: bondLadderData.account,
        qty: bondLadderData.qty,
        bondType: bondLadderData.bondType,
        nameOfTheLadder: bondLadderData.nameOfTheLadder,
        numberOfRungs: bondLadderData.numberOfRungs,
        amountPerRung: bondLadderData.amountPerRung,
        totalMaturity: bondLadderData.totalMaturity,
        // pmId: bondLadderData.pmId,
        userId: userId || null,
        type: bondLadderData.bondType, // Also include as 'type' field
        comments: bondLadderData.comments || "",
        // Stored under the spelling the backend reads: this JSON is replayed through the
        // filter API later, and only minCoupon/minMaturity/... are read there
        characteristics: JSON.stringify(toBackendFilterKeys(filteredCharacteristics)),
        // pm: bondLadderData.pm,
        userName: bondLadderData.userName,
        bondLadderId: ladderData.bondLadderId || ladderData.id,
      };

      // Call the update API
      const result = await updateBondLadder(token, updateData);

      // Clear ladder data on successful update
      resetBondLadderData();

      // Call parent onSubmit with updated data
      onSubmit(result);

      // Close modal
      onClose();
    } catch (error) {
      console.error("Error updating bond ladder:", error);  
      setError(error.message || "Failed to update ladder. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle validation changes from BondLadderTab
  const handleValidationChange = (hasErrors) => {
    setHasCharacteristicValidationErrors(hasErrors);
  };

  const handleClearAll = () => {
    //preserve pm and account
    const preservedData = {
      // pm: bondLadderData.pm,
      userName: bondLadderData.userName,
      account: bondLadderData.account,
    };
    resetBondLadderData();
    setBondLadderData(preservedData); // Reset to preserved data
    clearValidationErrors(); // Clear store validation errors
    setError("");
    setValidationErrors({});
    clearFieldErrors();
    setHasCharacteristicValidationErrors(false);

    // Clear window validation errors
    if (typeof window !== "undefined") {
      window.bondValidationErrors = {};
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Clear ladder data when closing via cross button
      resetBondLadderData();
      clearValidationErrors(); // Clear store validation errors
      setError("");
      setValidationErrors({});
      clearFieldErrors();
      setHasCharacteristicValidationErrors(false);

      // Clear window validation errors
      if (typeof window !== "undefined") {
        window.bondValidationErrors = {};
      }

      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={!loading ? handleClose : undefined}
      aria-labelledby="edit-ladder-modal-title"
    >
      <Box
        sx={{
          ...modalStyle,
          width: "66vw",
          height: "90vh",
          // maxWidth: "800px",
          maxHeight: "800px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            ...headerStyle,
            flexShrink: 0,
          }}
        >
          <Typography
            id="edit-ladder-modal-title"
            variant="h6"
            component="h2"
            sx={{ color: tokens.colors.fontColour, fontWeight: "bold" }}
          >
            Edit Ladder
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <BondLadderTab
            readOnly="edit" // Pass special edit mode
            accountIdentifiers={bondLadderData.account ? [bondLadderData.account] : []} // Prevent [undefined]
            errors={validationErrors}
            onValidationChange={handleValidationChange} // Pass validation change handler
          />
        </Box>

        {/* Footer */}
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            p: 2,
            borderTop: "1px solid rgba(255, 255, 255, 0.2)",
            backgroundColor: "background.paper",
          }}
        >
          {/* {error && (
            <Box sx={{ 
              mb: 2,
              p: 2,
              backgroundColor: "rgba(244, 67, 54, 0.1)",
              border: "1px solid rgba(244, 67, 54, 0.3)",
              borderRadius: "4px",
            }}>
              <Typography sx={{ 
                color: "rgba(244, 67, 54, 0.9)",
                fontSize: "14px",
                fontWeight: 500
              }}>
                {error}
              </Typography>
            </Box>
          )} */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleClearAll}
              disabled={loading}
              sx={{
                textTransform: "none",
                fontWeight: "700",
                px: 4,
                py: 1,
                width: "150px",
                height: "46px",
                borderRadius: "8px",
              }}
            >
              Clear All
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading || hasCharacteristicValidationErrors} // Disable button when validation errors exist
              sx={{
                textTransform: "none",
                fontWeight: "700",
                px: 4,
                py: 1,
                width: "180px",
                height: "46px",
                borderRadius: "8px",
                ...(hasCharacteristicValidationErrors && {
                  opacity: 0.6,
                  cursor: "not-allowed",
                }),
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Update Ladder"
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditBondLadder;
